import { useMemo } from 'react'
import type { ComponentProps } from 'react'
import { FilterChips } from '~/components/filterChips'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import {
  useAfternoonTea,
  useBuffet,
  useChildUse,
  useChristmas,
  useEnabledAfternoonTea,
  useEnabledBuffet,
  useEnabledChildUses,
  useEnabledChristmas,
  useEnabledFreeFlows,
  useEnabledSeatAttributes,
  useEnabledSmokingAllowed,
  useFreeFlows,
  useInitialPriceRange,
  useSeatAttributes,
  useSeatOnlyAfternoonTea,
  useSeatOnlyBuffet,
  useSeatOnlyChildUses,
  useSeatOnlyFreeFlows,
  useSeatOnlySeatAttributes,
  useSeatOnlySmokingAllowed,
  useShow,
  useShowSeatOnly,
  useSmokingAllowed,
} from '~/features/guide/courses/courseList'
import { getFragmentData } from '~/lib/graphql'
import { type AdditionalCriteria, useReserveOverlay, useReserveOverlayContext } from '~/navigation'
import {
  scope,
  useApplyPrice,
  useCalendarOpened,
  useClear,
  useFilterCriteria,
  useSetAfternoonTea,
  useSetBuffet,
  useSetChildUse,
  useSetChristmas,
  useSetFreeFlows,
  useSetSeatAttributes,
  useSetSmokingAllowed,
} from '~/stores/calendar/state'
import type { InitEvent } from '~/stores/calendar/statemachine'
import { useToday } from '~/stores/now'
import { DateTimeSelection } from './DateTimeSelection'
import { InitAtom } from './InitAtom'
import { RestaurantCalendar } from './RestaurantCalendar.frag'

export function DateTimeSelectionModal({
  mode,
}: { mode: ComponentProps<typeof DateTimeSelection>['mode'] }) {
  const [context, willBeUnmounted] = useReserveOverlay('CALENDAR_MODAL')

  const {
    restaurantId,
    planId,
    seatOnlyPlanIds,
    fromDate,
    openCalendarInitially,
    hideMealPeriodTab,
    visitorsCount,
    visitDate,
    visitTime,
    mealPeriod,
    childUses,
    seatTypes,
    smokingAllowed,
    windowSide,
    buffet,
    freeFlow,
    sceneTagIds,
    upperBoundPrice,
    lowerBoundPrice,
    isSeatOnly,
    // GuideQuery と互換性は持っているものの新ナビゲーションで得た値なのでリアルタイムに反映される
  } = context ?? {}

  const criteria: AdditionalCriteria = useMemo(
    () => ({
      childUses,
      seatTypes,
      smokingAllowed,
      windowSide,
      buffet,
      freeFlow,
      sceneTagIds,
      // 席のみはプラン価格が0円なため、予算を引き継いではいけない
      upperBoundPrice: isSeatOnly ? undefined : upperBoundPrice,
      lowerBoundPrice: isSeatOnly ? undefined : lowerBoundPrice,
    }),
    [
      buffet,
      childUses,
      isSeatOnly,
      freeFlow,
      lowerBoundPrice,
      sceneTagIds,
      seatTypes,
      smokingAllowed,
      upperBoundPrice,
      windowSide,
    ]
  )

  const { isLoading } = useReserveOverlayContext('SEAT_ONLY_MODAL') ?? {}
  const today = useToday()
  const opened = context !== undefined
  if (!opened) {
    return null
  }
  const { phoneNumber } = getFragmentData(RestaurantCalendar, context.restaurant) ?? {}
  const isPc = mode !== undefined && ['pc', 'pcModal'].includes(mode)

  const initEvent: InitEvent = {
    // 閉じている方が稀なパターンなので名前を入れ替えたが...
    calendarClosed: !openCalendarInitially,
    today,
    fromDate,
    restaurantId: restaurantId!,
    planIds: planId ? [planId] : seatOnlyPlanIds,
    phoneNumber,
    selectedVisitorsCount: visitorsCount,
    selectedVisitDate: visitDate,
    selectedVisitTime: visitTime,
    selectedMealPeriod: mealPeriod,
    criteria,
  }

  return (
    <scope.Provider>
      <StackableSlideUpModal
        willBeUnmounted={willBeUnmounted}
        type={isPc ? 'modal' : undefined}
        title={isPc ? '予約人数、日時の選択' : undefined}
        animationType={isPc ? 'fadeIn' : 'up'}
      >
        <DateTimeSelection
          mode={mode}
          hideMealPeriodTab={hideMealPeriodTab}
          isLoading={isLoading}
          filter={<Chips />}
        />
      </StackableSlideUpModal>
      <InitAtom initEvent={initEvent} />
    </scope.Provider>
  )
}

function Chips() {
  const showCalendar = useCalendarOpened()
  const context = useReserveOverlayContext('CALENDAR_MODAL')
  if (context === undefined || !showCalendar) {
    return null
  }

  if (context.isSeatOnly) {
    return (
      <FilterChips
        useFilterCriteria={useFilterCriteria}
        useFreeFlows={useSeatOnlyFreeFlows}
        useEnabledFreeFlows={useEnabledFreeFlows}
        useSetFreeFlows={useSetFreeFlows}
        useAfternoonTea={useSeatOnlyAfternoonTea}
        useSetAfternoonTea={useSetAfternoonTea}
        useEnabledAfternoonTea={useEnabledAfternoonTea}
        useBuffet={useSeatOnlyBuffet}
        useSetBuffet={useSetBuffet}
        useEnabledBuffet={useEnabledBuffet}
        useSmokingAllowed={useSeatOnlySmokingAllowed}
        useSetSmokingAllowed={useSetSmokingAllowed}
        useEnabledSmokingAllowed={useEnabledSmokingAllowed}
        useSeatAttributes={useSeatOnlySeatAttributes}
        useSetSeatAttributes={useSetSeatAttributes}
        useEnabledSeatAttributes={useEnabledSeatAttributes}
        useChildUse={useSeatOnlyChildUses}
        useSetChildUse={useSetChildUse}
        useEnabledChildUse={useEnabledChildUses}
        useApplyPrice={useApplyPrice}
        useInitialPriceRange={useInitialPriceRange}
        useChristmas={useChristmas}
        useSetChristmas={useSetChristmas}
        useEnabledChristmas={useEnabledChristmas}
        useClear={useClear}
        useShow={useShowSeatOnly}
      />
    )
  }

  return (
    <FilterChips
      useFilterCriteria={useFilterCriteria}
      useFreeFlows={useFreeFlows}
      useEnabledFreeFlows={useEnabledFreeFlows}
      useSetFreeFlows={useSetFreeFlows}
      useAfternoonTea={useAfternoonTea}
      useSetAfternoonTea={useSetAfternoonTea}
      useEnabledAfternoonTea={useEnabledAfternoonTea}
      useBuffet={useBuffet}
      useSetBuffet={useSetBuffet}
      useEnabledBuffet={useEnabledBuffet}
      useSmokingAllowed={useSmokingAllowed}
      useSetSmokingAllowed={useSetSmokingAllowed}
      useEnabledSmokingAllowed={useEnabledSmokingAllowed}
      useSeatAttributes={useSeatAttributes}
      useSetSeatAttributes={useSetSeatAttributes}
      useEnabledSeatAttributes={useEnabledSeatAttributes}
      useChildUse={useChildUse}
      useSetChildUse={useSetChildUse}
      useEnabledChildUse={useEnabledChildUses}
      useApplyPrice={useApplyPrice}
      useInitialPriceRange={useInitialPriceRange}
      useChristmas={useChristmas}
      useSetChristmas={useSetChristmas}
      useEnabledChristmas={useEnabledChristmas}
      useClear={useClear}
      useShow={useShow}
    />
  )
}
